"use client";

import { Stack, Box, useToast, Text } from "@chakra-ui/react";
import { OnboardingLayout } from "../layout/OnboardingLayout";
import { CustumSelect } from "../components/Input";
import * as Yup from "yup";
import { API_BASE_URL } from "../components/APIs";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import axios from "axios";
import { DriverLicenseType } from "../util/data";
import Cookies from "js-cookie";
import { useApiData } from "../components/GetData";
import FormNavigation from "../components/FormNavigation";
import FileUpload from "../components/FileUpload";
import PdfViewer from "../components/PDFViewer";

// Manual options to ensure consistency
const YES_NO_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" }
];

export default function MobilityStatus() {
  const {
    data: mobilityStatus,
    loading,
    fetchData,
  } = useApiData("application/getUserMobility");

  useEffect(() => {
    fetchData();
  }, []);

  const uid = Cookies.get("uid");
  const toast = useToast();
  const [isLoading, setLoading] = useState(false);
  const [isDLFileRequired, setDLFileRequired] = useState(false);
  const [pdfFile, setpdfFile] = useState();
  const formik = useFormik({
    initialValues: {
      dl_type: "",
      dl_file: "",
      car_owner: "",
      has_drivers_license: "",
      user_id: uid,
    },
    validationSchema: Yup.object({
      car_owner: Yup.string().required("Please Select an option"),
      has_drivers_license: Yup.string().required("Please Select an option"),
      dl_type: Yup.string().when('has_drivers_license', {
        is: 'yes',
        then: (schema) => schema.required("Please Select driver license type"),
        otherwise: (schema) => schema.notRequired(),
      }),
      dl_file: Yup.string().when('has_drivers_license', {
        is: 'yes',
        then: (schema) => {
          setDLFileRequired(true);
          return schema.required("Please upload driver license file");
        },
        otherwise: (schema) => {
          setDLFileRequired(false);
          return schema.notRequired();
        },
      }),

    }),
    onSubmit: (data) => {
      console.log("Submitting data:", data);
      setLoading(true);
      const url = API_BASE_URL + "application/saveMobility.php";
      axios
        .post(url, data)
        .then((response) => {
          if (response.data.status === 200) {
            toast({
              title: "Profile Update",
              description: response.data.message,
              position: "top",
              status: "success",
              isClosable: true,
            });
            setLoading(false);
          } else {
            toast({
              title: "Profile Update Error",
              description: response.data.message,
              position: "top",
              status: "error",
              isClosable: true,
            });
            setLoading(false);
          }
        })
        .catch((error) => {
          toast({
            title: "error",
            description: error.message,
            position: "top",
            status: "error",
            isClosable: true,
          });
          setLoading(false);
        });
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`Field changed: ${name} = "${value}"`);
    formik.handleChange(e);
  };

  useEffect(() => {
    if (mobilityStatus) {
      console.log("Loaded mobilityStatus:", mobilityStatus);
      const values = {
        dl_type: mobilityStatus.dl_type || "",
        car_owner: mobilityStatus.car_owner || "",
        has_drivers_license: mobilityStatus.has_drivers_license || "",
        user_id: mobilityStatus.user_id || uid,
        dl_file: mobilityStatus.dl_file || "",
      };
      console.log("Setting form values:", values);
      setpdfFile(API_BASE_URL + "uploads/" + mobilityStatus.dl_file || null);
      formik.setValues(values);
    }
  }, [mobilityStatus]);

  // Simple condition check
  const showLicenseType = formik.values.has_drivers_license === "yes";


  return (
    <OnboardingLayout
      pageTitle="Driving Status"
      mainContent={
        <>
          <Stack
            maxW={"2xl"}
            direction={{ base: "column", md: "column", sm: "column" }}
            as={"form"}
            onSubmit={formik.handleSubmit}
          >
            <input
              value={formik.values.user_id}
              onChange={handleChange}
              type="hidden"
              name="user_id"
            />

            <Stack gap={2} direction={["column", "column"]}>
              {/* Car ownership question */}
              <CustumSelect
                value={formik.values.car_owner}
                isInvalid={formik.errors.car_owner && formik.touched.car_owner}
                onChange={handleChange}
                name="car_owner"
                placeholder="Select"
                formLabel="Do you own a car?"
                options={[
                  { value: 1, label: "Yes" },
                  { value: 0, label: "No" }
                ]}
              />

              {/* Driver's license question - always shown */}
              <CustumSelect
                value={formik.values.has_drivers_license}
                isInvalid={formik.errors.has_drivers_license && formik.touched.has_drivers_license}
                onChange={handleChange}
                name="has_drivers_license"
                placeholder="Select"
                formLabel="Do you hold a valid driver's license?"
                options={YES_NO_OPTIONS}
              />

              {/* Driver license type - conditionally shown */}
              {showLicenseType && (
                <CustumSelect
                  value={formik.values.dl_type}
                  isInvalid={formik.errors.dl_type && formik.touched.dl_type}
                  onChange={handleChange}
                  name="dl_type"
                  placeholder="Select Driver License type"
                  formLabel="Nature of Driving Licence"
                  options={DriverLicenseType}
                />
              )}
            </Stack>
            {/* File Upload - Always Required */}
            {isDLFileRequired &&
              <Stack gap={2} direction={["column", "row"]}>
                <FileUpload
                  onFileUpload={(file) => formik.setFieldValue("dl_file", file)}
                  isRequired={true}
                  helperText={"Upload your Driver's License"}
                />
                {formik.errors.dl_file && formik.touched.dl_file && (
                  <Box color="red.500" fontSize="sm" mt={1}>
                    {formik.errors.dl_file}
                  </Box>
                )}
              </Stack>}

            <Stack gap={2} direction={["column", "column"]}>
              {pdfFile && <PdfViewer pdfUrl={pdfFile} />}
              <Box py={5} gap={5} display={"flex"} flexDir={"row"}>
                <FormNavigation
                  formik={formik}
                  isLoading={isLoading}
                  setIsLoading={setLoading}
                  nextRoute="/application/work-history"
                  previousRoute={'/application/next-of-kin'}
                  progressKey="mobility_status"
                />
              </Box>
            </Stack>
          </Stack>
        </>
      }
    />
  );
}